import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { fmtDate as fmtDmy } from "@/pages/employee/pnl/format";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";
import { yearOptions, MONTHS_RO } from "@/lib/years";

const WEEKDAY_RO = ["D", "L", "Ma", "Mi", "J", "V", "S"];

function ymd(d) { return d.toISOString().slice(0, 10); }

export default function ViewLeaveCalendar() {
  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1400px]">
        <div className="mb-8">
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand mb-2">Vizualizare</p>
          <h1 className="font-display font-semibold text-[38px] tracking-[-0.02em] leading-tight text-ink mb-2">Calendar concedii</h1>
          <p className="text-ink-muted text-sm">Cine e în concediu pe fiecare zi a lunii — doar cererile aprobate.</p>
        </div>
        <LeaveCalendarPanel />
      </div>
    </PortalLayout>
  );
}

export function LeaveCalendarPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/timeoff/calendar?month=${month}`);
      setItems(data || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const [y, m] = month.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
  const weekendDay = (d) => {
    const wd = new Date(y, m - 1, d).getDay();
    return wd === 0 || wd === 6;
  };

  // Rows per employee, cell = leave type for that day (color + name)
  const { rows, dayTotals } = useMemo(() => {
    const byEmp = {};
    const dayTot = {};
    const monthStart = new Date(y, m - 1, 1);
    const monthEnd = new Date(y, m - 1, daysInMonth);
    for (const r of items) {
      const start = new Date(r.start_date + "T00:00:00");
      const end = new Date(r.end_date + "T00:00:00");
      const s = new Date(Math.max(start, monthStart));
      const e = new Date(Math.min(end, monthEnd));
      const empId = r.employee?.id || r.user_id;
      if (!byEmp[empId]) {
        byEmp[empId] = {
          id: empId,
          name: r.employee?.name || "—",
          job_title: r.employee?.job_title || "",
          cells: {},
          total: 0,
        };
      }
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const wd = d.getDay();
        if (wd === 0 || wd === 6) continue; // fără concediu în weekend
        const day = d.getDate();
        if (!byEmp[empId].cells[day]) {
          byEmp[empId].cells[day] = {
            color: r.type?.color || "#3d68b1",
            type_name: r.type?.name || "Concediu",
          };
          byEmp[empId].total += 1;
          dayTot[day] = (dayTot[day] || 0) + 1;
        }
      }
    }
    const list = Object.values(byEmp).sort((a, b) => a.name.localeCompare(b.name));
    return { rows: list, dayTotals: dayTot };
  }, [items, y, m, daysInMonth]);

  // Cine e azi / săptămâna asta în concediu
  const { todayList, weekList } = useMemo(() => {
    const now = new Date();
    const today = ymd(now);
    const monday = new Date(now);
    const dow = (monday.getDay() + 6) % 7;
    monday.setDate(monday.getDate() - dow);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const inRange = (r, from, to) => r.start_date <= to && r.end_date >= from;
    const td = items.filter(r => inRange(r, today, today));
    const wk = items.filter(r => inRange(r, ymd(monday), ymd(sunday)));
    return { todayList: td, weekList: wk };
  }, [items]);

  // Legendă tipuri
  const legend = useMemo(() => {
    const map = {};
    for (const r of items) {
      if (r.type?.name) map[r.type.name] = r.type.color || "#3d68b1";
    }
    return Object.entries(map);
  }, [items]);

  return (
    <div>
        <div className="flex flex-wrap items-end gap-3 mb-6">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted block mb-1">Luna</label>
            <div className="flex gap-2">
              <select value={m} data-testid="cal-month"
                onChange={e => setMonth(`${y}-${String(Number(e.target.value)).padStart(2, "0")}`)}
                className="border border-neutral-300 px-3 py-2 text-sm bg-white min-w-[130px]">
                {MONTHS_RO.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
              </select>
              <select value={y} data-testid="cal-year"
                onChange={e => setMonth(`${e.target.value}-${String(m).padStart(2, "0")}`)}
                className="border border-neutral-300 px-3 py-2 text-sm bg-white">
                {yearOptions(y).map(yr => <option key={yr} value={yr}>{yr}</option>)}
              </select>
            </div>
          </div>
          {legend.length > 0 && (
            <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">
              {legend.map(([name, color]) => (
                <div key={name} className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
                  <span className="text-[12px] text-ink-muted">{name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-ink-muted">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-neutral-200 rounded-xl text-ink-muted" data-testid="cal-empty">
            Nimeni nu are concediu aprobat în luna selectată.
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 overflow-x-auto" data-testid="cal-grid">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-neutral-50">
                  <th className="sticky left-0 z-10 bg-neutral-50 text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-neutral-200 min-w-[200px]">
                    Angajat
                  </th>
                  {days.map(d => (
                    <th key={d} className={`px-1 py-1.5 text-center font-mono text-[11px] border-b border-neutral-200 w-9 ${weekendDay(d) ? "bg-neutral-100 text-ink-muted" : "text-ink"}`}>
                      <div>{d}</div>
                      <div className="text-[9px] text-ink-muted">{WEEKDAY_RO[new Date(y, m - 1, d).getDay()]}</div>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right font-mono text-[10px] uppercase tracking-widest text-brand border-b border-neutral-200 bg-neutral-50">
                    Zile
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`cal-emp-${r.id}`} className="hover:bg-ink-soft/40">
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 border-b border-neutral-100">
                      <div className="font-medium text-ink truncate">{r.name}</div>
                      <div className="text-[11px] text-ink-muted truncate">{r.job_title}</div>
                    </td>
                    {days.map(d => {
                      const cell = r.cells[d];
                      return (
                        <td key={d}
                          title={cell ? `${r.name} — ${cell.type_name}` : ""}
                          className={`px-1 py-2 text-center border-b border-neutral-100 ${weekendDay(d) ? "bg-neutral-50/60" : ""}`}>
                          {cell ? (
                            <span className="inline-block w-5 h-5 rounded-sm mx-auto"
                              style={{ background: cell.color }} />
                          ) : (
                            <span className="text-neutral-300">·</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-right font-display font-bold text-brand border-b border-neutral-100 tabular-nums">
                      {r.total}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-neutral-50">
                  <td className="sticky left-0 z-10 bg-neutral-50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                    Total / zi
                  </td>
                  {days.map(d => (
                    <td key={d} className={`px-1 py-2 text-center font-mono text-[11px] tabular-nums ${weekendDay(d) ? "bg-neutral-100" : ""} ${dayTotals[d] ? "text-ink font-semibold" : "text-neutral-300"}`}>
                      {dayTotals[d] || "·"}
                    </td>
                  ))}
                  <td className="px-3 py-2" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <LeavePanel title="Azi în concediu" list={todayList} testid="leave-today" />
          <LeavePanel title="Săptămâna aceasta" list={weekList} testid="leave-week" />
        </div>
    </div>
  );
}

function LeavePanel({ title, list, testid }) {
  return (
    <div className="bg-white border border-neutral-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-4 w-4 text-brand" />
        <h3 className="font-display font-semibold text-ink">{title}</h3>
        <span className="ml-auto text-xs font-mono text-ink-muted">{list.length}</span>
      </div>
      {list.length === 0 ? (
        <div className="text-sm text-ink-muted italic">Nimeni în concediu.</div>
      ) : (
        <div className="space-y-2">
          {list.map((r) => (
            <div key={r.id} className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: r.type?.color || "#3d68b1" }} />
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{r.employee?.name}</div>
                <div className="text-[11px] text-ink-muted truncate">{r.type?.name} · până {fmtDmy(r.end_date)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
